import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	NotificationChannel,
	type NotificationToggle,
	type NotificationType,
	type TenantConfigPlane,
} from '@today/types'
import type { NotificationToggleRow } from '@today/ui-domain'
import { useEffect, useState } from 'react'
import { notificationSettingsService } from '@/services/notification-settings.service'

/**
 * The Email section's notification switches (TO-382): the email column of the
 * tenant's toggle catalog, edited locally and saved as only the changed pairs.
 */
export function useNotificationToggles(
	tenantId: string,
	tenantSlug: string,
	plane: TenantConfigPlane,
	channel = NotificationChannel.EMAIL,
) {
	const client = useQueryClient()
	const key = ['notification-settings', 'toggles', tenantId, plane]
	const query = useQuery({
		queryKey: key,
		queryFn: () => notificationSettingsService.getToggles(tenantId, tenantSlug, plane),
	})
	const [local, setLocal] = useState<Partial<Record<NotificationType, boolean>>>({})

	useEffect(() => {
		if (query.data) setLocal({})
	}, [query.data])

	const stored: NotificationToggle[] = (query.data?.items ?? []).filter((item) => item.channel === channel)
	const rows: NotificationToggleRow[] = stored.map((item) => ({
		notificationType: item.notificationType,
		enabled: local[item.notificationType] ?? item.enabled,
		required: item.required,
	}))
	const changed = stored
		.filter((item) => local[item.notificationType] !== undefined && local[item.notificationType] !== item.enabled)
		.map((item) => ({ notificationType: item.notificationType, channel, enabled: !item.enabled }))

	const save = useMutation({
		mutationFn: () => notificationSettingsService.putToggles(tenantId, tenantSlug, plane, changed),
		onSuccess: (items) => client.setQueryData(key, { items }),
	})

	return {
		rows,
		loading: query.isPending,
		isBusy: save.isPending,
		saveFailed: save.isError,
		onToggle: (notificationType: NotificationType, enabled: boolean) =>
			setLocal((current) => ({ ...current, [notificationType]: enabled })),
		onSave: () => save.mutate(),
	}
}
